import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle, Users, Calendar, Lightbulb, Briefcase } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHapticFeedback } from '@/hooks/useMobileGestures';
import { useIsMobile } from '@/hooks/use-mobile';

interface FABAction {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  action: () => void;
  color?: string;
}

interface FloatingActionButtonProps {
  actions?: FABAction[];
  className?: string;
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  actions = [],
  className
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { lightImpact, mediumImpact } = useHapticFeedback();
  const isMobile = useIsMobile();

  if (!isMobile) return null;

  const defaultActions: FABAction[] = [
    {
      icon: MessageCircle,
      label: 'Message',
      action: () => {
        window.location.href = '/networking?tab=hub';
      },
      color: 'bg-blue-500'
    },
    {
      icon: Users,
      label: 'Connexion',
      action: () => {
        window.location.href = '/directory';
      },
      color: 'bg-green-500'
    },
    {
      icon: Calendar,
      label: 'Événement',
      action: () => {
        window.location.href = '/events';
      },
      color: 'bg-purple-500'
    },
    {
      icon: Lightbulb,
      label: 'Insight',
      action: () => {
        window.location.href = '/perks';
      },
      color: 'bg-yellow-500'
    },
    {
      icon: Briefcase,
      label: 'Opportunité',
      action: () => {
        window.location.href = '/marketplace';
      },
      color: 'bg-red-500'
    }
  ];

  const finalActions = actions.length > 0 ? actions : defaultActions;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    lightImpact();
  };

  const handleActionClick = (action: FABAction) => {
    action.action();
    setIsExpanded(false);
    mediumImpact();
  };

  return (
    <div className={cn(
      "fixed bottom-20 right-4 z-[45] flex flex-col-reverse items-end space-y-reverse space-y-3",
      className
    )}>
      {/* Backdrop */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm -z-10"
            onClick={() => setIsExpanded(false)}
          />
        )}
      </AnimatePresence>

      {/* Action buttons */}
      <AnimatePresence>
        {isExpanded && finalActions.map((action, index) => (
          <motion.button
            key={index}
            initial={{ opacity: 0, scale: 0, y: 20 }}
            animate={{ 
              opacity: 1, 
              scale: 1, 
              y: 0,
              transition: { delay: index * 0.05 }
            }}
            exit={{ 
              opacity: 0, 
              scale: 0, 
              y: 20,
              transition: { delay: (finalActions.length - index - 1) * 0.05 }
            }}
            onClick={() => handleActionClick(action)}
            className={cn(
              "flex items-center justify-center w-12 h-12 rounded-full shadow-lg",
              action.color || "bg-primary",
              "hover:scale-110 active:scale-95 transition-transform duration-200"
            )}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <action.icon className="h-6 w-6 text-white" />
          </motion.button>
        ))}
      </AnimatePresence>

      {/* Main FAB */}
      <motion.button
        onClick={toggleExpanded}
        className={cn(
          "flex items-center justify-center w-14 h-14 bg-primary rounded-full shadow-lg",
          "hover:scale-110 active:scale-95 transition-all duration-200"
        )}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={{ rotate: isExpanded ? 45 : 0 }}
      >
        <Plus className="h-7 w-7 text-primary-foreground" />
      </motion.button>

      {/* Labels */}
      <AnimatePresence>
        {isExpanded && finalActions.map((action, index) => (
          <motion.div
            key={`label-${index}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ 
              opacity: 1, 
              x: 0,
              transition: { delay: index * 0.05 + 0.1 }
            }}
            exit={{ opacity: 0, x: 20 }}
            className="absolute right-16 bg-card border border-border rounded-lg px-3 py-2 shadow-lg"
            style={{ 
              bottom: `${(finalActions.length - index) * 60 + 20}px`
            }}
          >
            <span className="text-sm font-medium text-foreground whitespace-nowrap">
              {action.label}
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default FloatingActionButton;